// BrandGuidelines.jsx
import React, { forwardRef } from 'react';
import {
    appName,
    contactEmail,
    contactNumber,
} from '../Constants';
import Footer from '../Footer';
import Navbar from '../Navbar';
import { useMenuItems } from '../menuItems';

const BrandGuidelines = forwardRef(() => {
    const { menuItems } = useMenuItems();
    return (
        <div className='font-sans antialiased bg-landing-white'>
            <Navbar menuItems={menuItems} />
            <section className='py-24 bg-gradient-to-br from-landing-slate-50 to-landing-white'>
                <div className='max-w-4xl mx-auto px-6'>
                    <div className='text-center mb-16'>
                        <span className='inline-flex items-center gap-2 bg-landing-blue-500/10 border border-landing-blue-500/20 rounded-full px-4 py-2 text-landing-blue-500 font-bold tracking-wide uppercase text-sm'>Brand</span>
                        <h1 className='text-4xl md:text-5xl font-bold text-landing-slate-900 mt-4 mb-6'>
                            Brand Guidelines
                        </h1>
                        <p className='text-xl text-landing-slate-600 max-w-2xl mx-auto leading-relaxed'>
                            How to represent Quyva consistently across all channels
                        </p>
                    </div>

                    <div className='bg-landing-white rounded-2xl p-8 shadow-sm border border-landing-gray-100'>
                        <p className='text-lg text-landing-slate-700 leading-relaxed mb-8'>
                            Welcome to {appName}! Our brand is important to us, and
                            we want to ensure that it is represented consistently
                            and effectively across all channels. These brand
                            guidelines outline how to use our logo, colors, fonts,
                            and other brand assets.
                        </p>

                        <h2 className='text-2xl font-bold text-landing-slate-900 mt-8 mb-4'>1. Logo Usage</h2>
                        <p className='text-landing-slate-700 leading-relaxed mb-4'>
                            Our logo is the primary visual representation of our
                            brand. When using our logo, please adhere to the
                            following guidelines:
                        </p>
                        <ul className='list-disc list-inside pl-4 text-landing-slate-700 space-y-2 mb-6'>
                            <li>
                                Use the logo in its original colors whenever
                                possible.
                            </li>
                            <li>
                                Do not alter the logo in any way, including
                                resizing, rotating, or distorting it.
                            </li>
                            <li>
                                Maintain clear space around the logo to ensure
                                visibility and legibility.
                            </li>
                            <li>
                                Do not place the logo on busy backgrounds or use
                                conflicting colors that may diminish its impact.
                            </li>
                        </ul>

                        <h2 className='text-2xl font-bold text-landing-slate-900 mt-8 mb-4'>2. Color Palette</h2>
                        <p className='text-landing-slate-700 leading-relaxed mb-6'>
                            Our brand colors are an essential part of our identity.
                            When using our brand colors, please ensure consistency
                            across all materials to maintain brand integrity.
                        </p>

                        <h2 className='text-2xl font-bold text-landing-slate-900 mt-8 mb-4'>3. Typography</h2>
                        <p className='text-landing-slate-700 leading-relaxed mb-6'>
                            Our brand typography is clean, modern, and easy to read.
                            The primary font for headings and titles is "Public
                            Sans", while the secondary font for body
                            text is "sans-serif."
                        </p>

                        <h2 className='text-2xl font-bold text-landing-slate-900 mt-8 mb-4'>4. Photography</h2>
                        <p className='text-landing-slate-700 leading-relaxed mb-6'>
                            When selecting imagery for our brand, choose
                            high-quality photos that align with our brand values and
                            aesthetics. Avoid using stock photos that appear generic
                            or unprofessional. Kindly do not use the logo without
                            our consent and for unethical purposes.
                        </p>

                        <h2 className='text-2xl font-bold text-landing-slate-900 mt-8 mb-4'>5. Voice and Tone</h2>
                        <p className='text-landing-slate-700 leading-relaxed mb-6'>
                            Our brand voice is friendly, informative, and
                            straightforward. When communicating with our audience,
                            strive for clarity and authenticity while maintaining a
                            positive tone.
                        </p>

                        <h2 className='text-2xl font-bold text-landing-slate-900 mt-8 mb-4'>6. Contact Us</h2>
                        <p className='text-landing-slate-700 leading-relaxed'>
                            If you have any questions or need further assistance
                            with our brand guidelines, please contact us at{' '}
                            {contactNumber ? <><a href={`tel:${contactNumber}`} className='text-landing-blue-600 hover:underline'>{contactNumber}</a> or </> : ''}
                            <a href={`mailto:${contactEmail}`} className='text-landing-blue-600 hover:underline'>{contactEmail}</a>.
                        </p>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
});

export default BrandGuidelines;
